import {config} from "../../../../../config";

export class SedestralProactiveGoogle {
    private static installed = false;
    private static consentGranted = false;
    private static readonly TRAFFIC_SOURCE_KEY = '_sdv';
    private static readonly COOKIE_DOMAIN = '.sedestral.com';
    private static readonly COOKIE_DAYS = 90;

    static async init(): Promise<void> {
        if (config.isWhiteLabel) return;
        const gtmId = config.gtmId;
        if (!gtmId) return;
        try {
            this.initTrafficSource();

            const w = window as any;
            w.dataLayer = w.dataLayer || [];

            function gtag(...args: any[]) {
                w.dataLayer.push(arguments);
            }

            w.gtag = gtag;

            gtag('consent', 'default', {
                ad_storage: 'denied',
                analytics_storage: 'denied',
            });

            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
            w.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});

            await new Promise<void>((resolve, reject) => {
                script.onload = () => resolve();
                script.onerror = () => reject(new Error('GTM script failed to load'));
                document.head.appendChild(script);
            });

            const noscript = document.createElement('noscript');
            const iframe = document.createElement('iframe');
            iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
            iframe.height = '0';
            iframe.width = '0';
            iframe.style.display = 'none';
            iframe.style.visibility = 'hidden';
            noscript.appendChild(iframe);
            document.body.insertBefore(noscript, document.body.firstChild);

            gtag('config', config.googleAdsId, {
                linker: {
                    domains: ['sedestral.com', 'panel.sedestral.com'],
                },
            });

            this.installed = true;
            this.grantConsent();
        } catch (err) {
            console.error(err);
        }
    }

    static grantConsent(): void {
        try {
            this.consentGranted = true;
            const w = window as any;
            if (w.gtag) {
                w.gtag('consent', 'update', {
                    ad_storage: 'granted',
                    analytics_storage: 'granted',
                });
            }
        } catch (err) {
            console.error(err);
        }
    }

    static trackEvent(eventName: string, params?: Record<string, any>): void {
        try {
            if (!this.installed || !this.consentGranted) return;
            const w = window as any;
            w.dataLayer.push({event: eventName, ...params});
        } catch (err) {
            console.error(err);
        }
    }

    static trackPageView(pagePath?: string): void {
        try {
            if (!this.installed || !this.consentGranted) return;
            const w = window as any;
            w.dataLayer.push({
                event: 'page_view',
                page_path: pagePath || window.location.pathname,
            });
        } catch (err) {
            console.error(err);
        }
    }

    static trackSignup(): void {
        try {
            if (!this.installed || !this.consentGranted) return;
            const w = window as any;
            w.gtag('event', 'conversion', {
                send_to: `${config.googleAdsId}/${config.googleAdsSignupLabel}`,
                value: 1,
                currency: 'EUR',
            });
        } catch (err) {
            console.error(err);
        }
    }

    static trackPurchase(amount: number, transactionId: string, currency: string = 'EUR'): void {
        try {
            if (!this.installed || !this.consentGranted) return;
            const w = window as any;
            w.gtag('event', 'conversion', {
                send_to: `${config.googleAdsId}/${config.googleAdsPurchaseLabel}`,
                value: amount,
                currency: currency,
                transaction_id: transactionId,
            });
        } catch (err) {
            console.error(err);
        }
    }

    static trackSchedule(): void {
        try {
            if (!this.installed || !this.consentGranted) return;
            const w = window as any;
            w.gtag('event', 'conversion', {
                send_to: `${config.googleAdsId}/${config.googleAdsScheduleLabel}`,
                value: 1,
                currency: 'EUR',
            });
        } catch (err) {
            console.error(err);
        }
    }

    private static initTrafficSource(): void {
        try {
            if (this.getCookie(this.TRAFFIC_SOURCE_KEY)) return;

            const urlParams = new URLSearchParams(window.location.search);
            const referrer = document.referrer;

            let source = 'direct';
            let medium = 'none';
            let campaign: string | null = null;

            if (urlParams.get('utm_source')) {
                source = urlParams.get('utm_source')!;
                medium = urlParams.get('utm_medium') || 'unknown';
                campaign = urlParams.get('utm_campaign');
            } else if (urlParams.get('fbclid')) {
                source = 'meta';
                medium = 'cpc';
            } else if (urlParams.get('gclid')) {
                source = 'google';
                medium = 'cpc';
            } else if (urlParams.get('msclkid')) {
                source = 'bing';
                medium = 'cpc';
            } else if (referrer) {
                const referrerUrl = new URL(referrer);
                const hostname = referrerUrl.hostname;

                if (hostname.includes('google.')) {
                    source = 'google';
                    medium = 'organic';
                } else if (hostname.includes('facebook.') || hostname.includes('fb.') || hostname.includes('instagram.')) {
                    source = 'meta';
                    medium = 'referral';
                } else if (hostname.includes('twitter.') || hostname.includes('t.co') || hostname.includes('x.com')) {
                    source = 'twitter';
                    medium = 'referral';
                } else if (hostname.includes('linkedin.')) {
                    source = 'linkedin';
                    medium = 'referral';
                } else if (hostname.includes('bing.')) {
                    source = 'bing';
                    medium = 'organic';
                } else if (hostname.includes('yahoo.')) {
                    source = 'yahoo';
                    medium = 'organic';
                } else {
                    source = hostname;
                    medium = 'referral';
                }
            }

            const trafficData = {
                source,
                medium,
                campaign,
                timestamp: Date.now(),
                url: window.location.href
            };

            this.setCookie(this.TRAFFIC_SOURCE_KEY, JSON.stringify(trafficData), this.COOKIE_DAYS);
        } catch (err) {
            console.error(err);
        }
    }

    static getTrafficSource(): { source: string; medium: string; campaign: string | null; timestamp: number; url: string } | null {
        try {
            const stored = this.getCookie(this.TRAFFIC_SOURCE_KEY);
            if (!stored) return null;
            return JSON.parse(stored);
        } catch (err) {
            console.error(err);
            return null;
        }
    }

    private static setCookie(name: string, value: string, days: number): void {
        const expires = new Date();
        expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
        const secure = window.location.protocol === 'https:' ? ';Secure' : '';
        document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};domain=${this.COOKIE_DOMAIN};path=/;SameSite=Lax${secure}`;
    }

    private static getCookie(name: string): string | null {
        const nameEQ = name + '=';
        const cookies = document.cookie.split(';');
        for (const cookie of cookies) {
            const trimmed = cookie.trim();
            if (trimmed.indexOf(nameEQ) === 0) {
                return decodeURIComponent(trimmed.substring(nameEQ.length));
            }
        }
        return null;
    }
}
